import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ContactModel,
  ContactStore,
  CONTACT_FILTER_TYPE,
} from '@boxx/contacts-core';
import { DynamicBrandingService } from '@boxx/dynamic-branding/lib/services/dynamic-branding-service';
import {
  ActionSheetController,
  AlertController,
  IonSearchbar,
  ModalController,
  Platform,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BoxxUtilsService } from 'src/services/BoxxUtils.service';
import { FirebaseAnalyticsService } from 'src/services/NativeFirebase/NativeFirebase.service';
import { SharingService } from 'src/services/NativeSharing.service';
import { PwaUtilsService } from 'src/services/pwaUtils.service';

@Component({
  selector: 'app-listcontacts',
  templateUrl: './list-contacts.page.html',
  styleUrls: ['./list-contacts.page.scss'],
})
export class ListContactsPage implements OnInit, OnDestroy {
  @ViewChild('contactsSB', { static: false }) searchBar: IonSearchbar;

  showSearchBar = false;
  searchValue = '';
  totalContacts = 0;
  totalNotSpecified = 0;
  totalProspect = 0;
  totalClient = 0;

  selectedMessage = 0;
  contactId: number;

  filteredContacts$: Observable<ContactModel[]>;
  loading$: Observable<boolean>;
  destroyed$ = new Subject<boolean>();

  loadedContacts: ContactModel[];
  searchResults: ContactModel[] = [];

  translations: any = {};

  filterType: CONTACT_FILTER_TYPE = 'ALL';

  contactTypes = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    PROSPECT: 'Prospect',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    CLIENT: 'Client',
    // eslint-disable-next-line @typescript-eslint/naming-convention
    NOT_SPECIFIED: 'Not Qualified',
  };

  isIos = true;

  isCordovaOrCapacitor = false;

  breakpointWidth: number;
  isPwaView = false;
  defaultPrimaryColor: string;
  brandName: string;

  // creationalPermissions = {
  //   [ContactsPermissionsProps.contactCreate]: false,
  //   [ContactsPermissionsProps.contactImport]: false,
  // };

  constructor(
    private store: ContactStore,
    private router: Router,
    private actionSheetCtrl: ActionSheetController,
    private translate: TranslateService,
    private shareSrvc: SharingService,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    public boxxUtils: BoxxUtilsService,
    private plt: Platform,
    private route: ActivatedRoute,
    private firebaseService: FirebaseAnalyticsService,
    private pwaUtilsService: PwaUtilsService
  ) {
    this.isIos = this.plt
      .platforms()
      .some((name) => ['ios', 'ipad', 'iphone'].includes(name));

    this.isCordovaOrCapacitor =
      this.plt.is('cordova') || this.plt.is('capacitor');

    this.route.queryParams.subscribe((params) => {
      if (params && params.tab) {
        this.filterType = params.tab;
      }
    });

    //this.setPermissions();
  }

  ngOnInit() {
    this.filteredContacts$ = this.store.contacts.filteredContacts$;

    this.loading$ = this.store.contacts.loading$;

    this.filteredContacts$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cList) => {
        this.searchResults = cList;
        this.loadedContacts = cList;
        this.totalContacts = 0;

        if (this.searchResults && this.searchResults.length > 0) {
          this.totalContacts = this.searchResults.length;
          this.totalNotSpecified = this.searchResults.filter(
            (value) => value.type === 'NOT_SPECIFIED'
          ).length;
          this.totalProspect = this.searchResults.filter(
            (value) => value.type === 'PROSPECT'
          ).length;
          this.totalClient = this.searchResults.filter(
            (value) => value.type === 'CLIENT'
          ).length;
        }
      });

    this.store.contacts.data$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.filterBy(this.filterType));

    this.store.contacts.fetched$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fetched) => {
        if (!fetched) {
          this.store.contacts.fetch();
        }
      });

    this.translate
      .get(['ACTIONS', 'CONTACTS_MODULE', 'ERRORS', 'SUCCESS'])
      .pipe(takeUntil(this.destroyed$))
      .subscribe((t: any) => {
        this.translations = t;
        this.contactTypes.PROSPECT = t.CONTACTS_MODULE.contactFilters.prospect;
        this.contactTypes.CLIENT = t.CONTACTS_MODULE.contactFilters.client;
        this.contactTypes.NOT_SPECIFIED =
          t.CONTACTS_MODULE.contactFilters.not_specified;
      });

    this.translate.onLangChange.subscribe(({ translations }) => {
      this.translations = translations;
      this.contactTypes.PROSPECT =
        translations.CONTACTS_MODULE.contactFilters.prospect;
      this.contactTypes.CLIENT =
        translations.CONTACTS_MODULE.contactFilters.client;
      this.contactTypes.NOT_SPECIFIED =
        translations.CONTACTS_MODULE.contactFilters.not_specified;
    });

    this.handleError();
    this.handleSuccess();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ionViewDidEnter() {
    this.firebaseService.setScreenName('CONTACTS');
    //this.setPwaListener();
  }

  filterBy(criteria: any = this.filterType) {
    this.searchValue = ' ';
    setTimeout(() => {
      this.searchValue = '';
    }, 10);

    this.showSearchBar = false;
    this.store.contacts.filter(criteria);
  }
  
  close() {
    this.modalCtrl.dismiss();
  }

  async showAddContactOpts() {
    const buttons = [];

    if (this.isCordovaOrCapacitor) {
      buttons.push({
        text: this.translations.CONTACTS_MODULE.actionSheet_VIEW.importItem,
        handler: () => {
          this.router.navigateByUrl('/tabs/contacts/import');
        },
      });
    }

    (
      await this.actionSheetCtrl.create({
        header: this.translations.CONTACTS_MODULE.actionSheet_VIEW.mainHeader,
        buttons: [
          ...buttons,
          {
            text: this.translations.CONTACTS_MODULE.actionSheet_VIEW.createItem,
            handler: () => {
              this.presentModal();
            },
          },
          {
            text: this.translations.ACTIONS.cancel,
            role: 'cancel',
            handler: () => {},
          },
        ],
      })
    ).present();
  }

  async confirmDelete(id: number, slidingItem: any) {
    slidingItem.close();

    this.boxxUtils.showAlert({
      header: this.translations.CONTACTS_MODULE.deleteALERT.header,
      message: this.translations.CONTACTS_MODULE.deleteALERT.message,
      buttons: [
        {
          text: this.translations.ACTIONS.cancel,
          cssClass: 'boxx-main-btn',
        },
        {
          text: this.translations.ACTIONS.ok,
          handler: () => {
            this.store.contacts.delete(id);
          },
        },
      ],
    });
  }

  async openEditorModal(contact: ContactModel, slidingItem: any) {
    slidingItem.close();

    this.router.navigateByUrl('tabs/contacts/detail/' + contact.id);
  }

  makeCall(phoneNumber: string) {
    this.shareSrvc.makeCall(phoneNumber);
  }

  sendMail(email: string) {
    this.shareSrvc.sendEmail(email);
  }

  async sendWhatsapp(contact: ContactModel) {
    if (contact.phone.length !== 10) {
      (
        await this.alertCtrl.create({
          header: this.translations.ACTIONS.confirm,
          message: this.translations.CONTACTS.confirmUpdateContactPhoneMsg,
          buttons: [
            {
              text: this.translations.ACTIONS.cancel,
              role: 'cancel',
            },
            {
              text: this.translations.ACTIONS.ok,
              handler: () => this.presentModal({ mode: 'UPDATE', contact }),
            },
          ],
        })
      ).present();

      return;
    }

    this.shareSrvc
      .whatsAppSendMessage(
        contact.phone,
        contact.countryCode,
        contact.phoneCode,
        this.translations.FORMS.hi
      )
      .then(
        async () => {
          this.boxxUtils.showToastSuccess({
            message: this.translations.SUCCESS.CONTACTS.actionCompleted,
          });
        },
        async (e) => {
          this.boxxUtils.showToastError({
            message: `${this.translations.ERRORS.CONTACTS[e.after]} <br> ${
              e.error
            }`,
          });
        }
      );
  }

  search(ev: any) {
    const searchTerm = ev.detail.value;
    this.searchResults = this.loadedContacts;

    if (!searchTerm) {
      return;
    }

    this.searchResults = this.searchResults.filter(
      (contact) =>
        contact.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1 ||
        contact.phone.indexOf(searchTerm) > -1
    );
  }

  onCancelSearch() {
    this.showSearchBar = false;
  }

  async presentModal(
    options: { mode: 'CREATE' | 'UPDATE'; contact: ContactModel } = {
      mode: 'CREATE',
      contact: ContactModel.empty(),
    }
  ) {
    // const modal = await this.modalCtrl.create({
    //   component: ContactUpsertModalComponent,
    //   componentProps: {
    //     mode: options.mode,
    //     contact: options.contact,
    //   },
    // });

    // this.firebaseService.setScreenName('CONTACTS > ' + options.mode);

    // await modal.present();

    // const { data } = await modal.onWillDismiss();

    // if (options.mode === 'CREATE') {
    //   this.filterType = 'ALL';
    // }
  }

  doRefresh(event) {
    //if (this.permissionsService.getPermission('contactList')) {
      this.store.contacts.fetch();
    //}
    setTimeout(() => {
      event.target.complete();
    }, 100);
  }

  private handleSuccess() {
    this.store.contacts.success$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((success) => {
        if (success && success?.after !== 'GET') {
          this.boxxUtils.showToastSuccess({
            message:
              this.translations.SUCCESS.CONTACTS[success.after.toLowerCase()],
          });
        }
      });
  }

  private handleError() {
    this.store.contacts.error$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (e) => {
        if (e) {
          const toast = await this.boxxUtils.showToastError({
            message: this.translations.ERRORS.CONTACTS[e.after.toLowerCase()],
          });
        }
      });
  }

  // private setPermissions() {
  //   Object.keys(this.permissionsService.getPermissions()).forEach((key) => {
  //     if (key === ContactsPermissionsProps.contactImport) {
  //       this.creationalPermissions[key] = this.isCordovaOrCapacitor;
  //     } else {
  //       this.creationalPermissions[key] = true;
  //     }
  //   });
  // }

  openPage(item){
    this.modalCtrl.dismiss({ contact: item });
  }

}
